// src/pages/ExploreGuides.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { endpoints } from '../services/api';
import GuideCard from '../components/GuideCard/GuideCard';
import SkeletonCard from '../components/GuideCard/SkeletonCard';
import '../styles/pages/Page.css';
import '../styles/pages/Explore.css';
import { getFriendlyError } from '../components/errorUtils';

const PAGE_SIZE = 9;

const ExploreGuides = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // committed search value

  const skipRef = useRef(0);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const isFetchingRef = useRef(false);

  // -------------------------------------------------------------------------
  // Fetch one page — takes the search string explicitly so resets work cleanly
  // -------------------------------------------------------------------------
  const fetchGuides = useCallback(async (skip, search) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    skip === 0 ? setLoading(true) : setLoadingMore(true);
    setError('');

    try {
      const url = `${endpoints.guides.public}?skip=${skip}&limit=${PAGE_SIZE}&search=${encodeURIComponent(search)}`;
      const res = await fetch(url);

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to fetch guides');
      }

      const { items, has_more } = await res.json();

      setHasMore(has_more);
      setGuides(prev => skip === 0 ? items : [...prev, ...items]);
      skipRef.current = skip + items.length;
    } catch (err) {
      setError(getFriendlyError(err.message, 'Explore Guides'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Initial load + reset when activeSearch changes
  // -------------------------------------------------------------------------
  useEffect(() => {
    window.scrollTo(0, 0);
    skipRef.current = 0;
    setGuides([]);
    setHasMore(true);
    fetchGuides(0, activeSearch);
  }, [activeSearch, fetchGuides]);

  // -------------------------------------------------------------------------
  // IntersectionObserver
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchGuides(skipRef.current, activeSearch);
        }
      },
      { rootMargin: '800px' }
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, activeSearch, fetchGuides]);

  // -------------------------------------------------------------------------
  // Search — commit only on submit so typing doesn't fire rapid fetches
  // -------------------------------------------------------------------------
  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchTerm.trim());
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Clear search instantly when input is emptied
    if (e.target.value === '') setActiveSearch('');
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="page-container">
      <div className="container">
        <header className="m-page-head explore-head">
          <div>
            <p className="m-eyebrow">Public library</p>
            <h1>Explore all guides</h1>
            <p>Guides other people made public. Open one and press play to follow it on the real site.</p>
          </div>
          <form onSubmit={handleSearch} className="explore-search" role="search">
            <label htmlFor="explore-search-input" className="explore-search__label">Search guides</label>
            <input
              id="explore-search-input"
              type="search"
              className="explore-search__input"
              placeholder="Search by name or description…"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <button type="submit" className="lp-btn sm">Search</button>
          </form>
        </header>

        {error && <p className="page-error">{error}</p>}

        <div className="guide-grid">
          {/* Initial skeleton */}
          {loading &&
            Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <SkeletonCard key={`sk-${i}`} cardIndex={i % PAGE_SIZE} />
            ))
          }

          {!loading && !error && guides.length === 0 && (
            <p className="explore-empty">No guides match that search. Try a different word, or record your own.</p>
          )}

          {guides.map((guide, i) => (
            <GuideCard key={guide.id} guide={guide} cardIndex={i % PAGE_SIZE} blurPreview />
          ))}

          {/* Load-more skeletons */}
          {loadingMore &&
            Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={`sk-more-${i}`} cardIndex={i % PAGE_SIZE} />
            ))
          }
        </div>

        {/* Sentinel */}
        {!loading && !error && (
          <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
        )}

        {/* End of list */}
        {!loading && !hasMore && guides.length > 0 && (
          <p className="guides-end-label">All guides loaded</p>
        )}
      </div>
    </div>
  );
};

export default ExploreGuides;